import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    CapsuleButton,
    MainPageWithButtons,
    SegmentedControl,
} from "../../Common/_components";
import { Icons } from "../../Common/_assets/icons/Icons";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import { OrganizationDetail } from "../../Organizations/_models/Organization";
import { CustomModal } from "../../Everou/_components/CustomModal";
import { PMSOrganizationMembers } from "./PMSOrganizationMembers";
import { PMSOrganizationLocations } from "./PMSOrganizationLocations";
import { useOrganizationPermissions } from "../_helpers/organizationPermissions";
import { PMSOrganizationConfiguration } from "./PMSOrganizationConfiguration";
import { PMSIntegrationsModule } from "../ModuleIntegrations/PMSIntegrationsModule";
import { PMSCustomizationModule } from "../ModuleCustomization/PMSCustomizationModule";
import { PMSActiveOrganizationStore } from "../_stores/PMSActiveOrganizationStore";
import { PMSOrganizationTabStore } from "../_stores/PMSOrganizationTabStore";

type Tab =
    | "MEMBERS"
    | "LOCATIONS"
    | "ACCESS"
    | "ALARM"
    | "INTEGRATIONS"
    | "CUSTOMIZATION";

type Props = {
    onGoBack?: () => void;
};

export function PMSOrganizationDetail({ onGoBack }: Props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const selectedUid: string | null = useSelector(
        Selectors.getOrganizationSelectedUid,
    );
    const detail: OrganizationDetail | null = useSelector(
        Selectors.getOrganizationSelectedDetail,
    );
    const isRequesting: boolean = useSelector(
        Selectors.getOrganizationDetailIsRequesting,
    );

    // Locations first: an organization needs at least one location before
    // members can be added. A shortcut from elsewhere may ask for another tab
    // (the location's "go to template"); its request is consumed here.
    const requestedTab: Tab | null = useSelector(
        Selectors.getPMSOrganizationRequestedTab,
    );
    const [tab, setTab] = useState<Tab>(requestedTab ?? "LOCATIONS");
    useEffect(() => {
        if (!requestedTab) return;
        // Also honoured while already mounted, not just at mount.
        setTab(requestedTab);
        dispatch(PMSOrganizationTabStore.actionSet(null));
    }, [dispatch, requestedTab]);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [settingsName, setSettingsName] = useState("");

    // Only the main manager can delete the organization (backend error 776)
    // or administer it — renaming included (backend error 780).
    const { isOwner, canManage } = useOrganizationPermissions();

    useEffect(() => {
        if (selectedUid)
            dispatch(OrganizationsActions.getOrganization(selectedUid));
    }, [dispatch, selectedUid]);

    // The Integrations and Customization tabs are the organization-centric
    // modules embedded, and those operate on the active organization: keep it
    // the one on screen (the auto-select of a single organization doesn't set
    // it) so they never edit another organization's templates.
    useEffect(() => {
        if (selectedUid)
            dispatch(PMSActiveOrganizationStore.actionSet(selectedUid));
    }, [dispatch, selectedUid]);

    if (!selectedUid) return null;

    function openSettings() {
        setSettingsName(detail?.name ?? "");
        setIsSettingsOpen(true);
    }

    function handleSaveSettings() {
        if (!selectedUid || !canManage) return;
        const trimmed = settingsName.trim();
        if (!trimmed) return;
        dispatch(OrganizationsActions.updateOrganization(selectedUid, trimmed));
        setIsSettingsOpen(false);
    }

    function handleDelete() {
        if (!selectedUid) return;
        dispatch(OrganizationsActions.deleteOrganization(selectedUid));
        setIsDeleteOpen(false);
    }

    return (
        <>
            <MainPageWithButtons
                // Same width as the location detail: the Integrations tab
                // embeds the catalog next to its detail panel.
                size={"X_LARGE"}
                pageTitle={pageTitle()}
                content={content()}
                buttons={
                    // Without the detail there is no name to edit yet.
                    detail
                        ? [
                              <CapsuleButton
                                  testId={"organization-settings"}
                                  icon={Icons.gear}
                                  style={"INFO"}
                                  isEnabled={canManage}
                                  tooltip={
                                      canManage
                                          ? t("global_settings")
                                          : t(
                                                "error_780_organization_requires_owner",
                                            )
                                  }
                                  boostedToolTip={!canManage}
                                  onClick={openSettings}
                              />,
                          ]
                        : []
                }
            />
            <CustomModal
                show={isSettingsOpen}
                onHide={() => setIsSettingsOpen(false)}
                title={t("global_settings")}
                content={
                    <div className="d-flex flex-column">
                        <label className="font-weight-bold text-uppercase small mb-1">
                            {t("mod_organizations_name")}
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={settingsName}
                            autoFocus
                            placeholder={t(
                                "mod_organizations_name_placeholder",
                            )}
                            onChange={(e) => setSettingsName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveSettings();
                            }}
                        />
                        {isOwner && (
                            <>
                                <hr className="w-100 my-4" />
                                <label className="font-weight-bold text-uppercase small text-danger mb-1">
                                    {t("mod_organizations_delete")}
                                </label>
                                <div className="text-muted small mb-3">
                                    {t("mod_organizations_delete_description")}
                                </div>
                                <div>
                                    <CapsuleButton
                                        text={t("mod_organizations_delete")}
                                        style={"DANGER"}
                                        onClick={() => {
                                            setIsSettingsOpen(false);
                                            setIsDeleteOpen(true);
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                }
                footer={
                    <div className="d-flex">
                        <CapsuleButton
                            text={t("global_cancel")}
                            style={"INFO"}
                            onClick={() => setIsSettingsOpen(false)}
                        />
                        <div className="ml-2" />
                        <CapsuleButton
                            testId={"organization-settings-save"}
                            text={t("global_save")}
                            onClick={handleSaveSettings}
                            isEnabled={!!settingsName.trim() && canManage}
                        />
                    </div>
                }
            />
            <CustomModal
                show={isDeleteOpen}
                onHide={() => setIsDeleteOpen(false)}
                title={t("mod_organizations_delete")}
                content={
                    <div>
                        {t("mod_organizations_delete_confirm", {
                            name: detail?.name ?? "",
                        })}
                    </div>
                }
                footer={
                    <div className="d-flex">
                        <CapsuleButton
                            text={t("global_cancel")}
                            style={"INFO"}
                            onClick={() => setIsDeleteOpen(false)}
                        />
                        <div className="ml-2" />
                        <CapsuleButton
                            text={t("mod_organizations_delete")}
                            style={"DANGER"}
                            onClick={handleDelete}
                        />
                    </div>
                }
            />
        </>
    );

    function pageTitle() {
        if (!onGoBack) return detail?.name ?? "";

        return (
            <div className="d-flex align-items-center">
                <img
                    src={Icons.previousIcon}
                    alt="back icon"
                    onClick={onGoBack}
                    className="c-img-2rem mr-2 cp c-hover-230 rounded-circle"
                />
                {detail?.name ?? ""}
            </div>
        );
    }

    function content() {
        if (!detail) {
            if (isRequesting) return spinner();
            return loadError();
        }
        return (
            <div className="d-flex flex-column h-100">
                {tabs()}
                <div className="mt-3 flex-fill overflow-auto">
                    {tab === "LOCATIONS" && <PMSOrganizationLocations />}
                    {tab === "MEMBERS" && <PMSOrganizationMembers />}
                    {tab === "INTEGRATIONS" && (
                        <PMSIntegrationsModule isEmbedded />
                    )}
                    {tab === "CUSTOMIZATION" && (
                        <PMSCustomizationModule isEmbedded />
                    )}
                    {(tab === "ACCESS" || tab === "ALARM") && (
                        <PMSOrganizationConfiguration
                            organizationUid={detail.uid}
                            settings={detail.locationSettings}
                            section={tab}
                        />
                    )}
                </div>
            </div>
        );
    }

    function tabs() {
        return (
            <SegmentedControl
                preventDefaultSelect
                defaultSelected={{ value: tab }}
                onSelect={(value) => setTab(value as Tab)}
                segments={[
                    {
                        label: t("mod_organizations_locations"),
                        value: "LOCATIONS",
                    },
                    { label: t("mod_organizations_members"), value: "MEMBERS" },
                    {
                        label: t("mod_pms_accounts_title"),
                        value: "INTEGRATIONS",
                    },
                    {
                        label: t("mod_pms_customization"),
                        value: "CUSTOMIZATION",
                    },
                    {
                        label: t("mod_pms_location_public_link_section_title"),
                        value: "ACCESS",
                    },
                    {
                        label: t("mod_pms_location_alarm_tab"),
                        value: "ALARM",
                    },
                ]}
            />
        );
    }

    function loadError() {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-4">
                <h5 className="text-center">{t("global_error_network")}</h5>
                <div className="mt-3">
                    <CapsuleButton
                        text={t("global_retry")}
                        onClick={() => {
                            if (selectedUid) {
                                dispatch(
                                    OrganizationsActions.getOrganization(
                                        selectedUid,
                                    ),
                                );
                            }
                        }}
                    />
                </div>
            </div>
        );
    }

    function spinner() {
        return (
            <div className="w-100 pt-2 text-center">
                <div className="spinner-border c-text-primary" />
            </div>
        );
    }
}
